import React, { useState } from 'react';
import API from '../../../utils/api';

// DEMO_USERS removed

export default function LoginPage({ onLogin }) {
    const [mode, setMode] = useState('login'); // 'login' or 'register' or 'forgot'
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role] = useState('customer'); // Always customer for public signup
    const [showPwd, setShowPwd] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState('');
    const [token, setToken] = useState('');

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const vt = urlParams.get('verify');
        const rt = urlParams.get('token');
        
        if (vt) {
            handleVerify(vt);
        } else if (rt) {
            setToken(rt);
            setMode('reset-password');
        }
    }, []);

    const handleVerify = async (vtoken) => {
        setLoading(true);
        try {
            await API.post('/auth/verify', { token: vtoken });
            setError('✅ Email verified successfully! You can now login.');
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Verification failed. Link might be expired.');
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (!password) { setError('Please enter a new password'); return; }
        if (password.length < 8 || !/[A-Z]/.test(password)) {
            setError('Password must be 8+ chars & include an uppercase letter');
            return;
        }
        setLoading(true);
        try {
            await API.post('/auth/reset-password', { token, newPassword: password });
            setError('✅ Password reset successfully! Redirecting to login...');
            setTimeout(() => {
                setMode('login');
                setError('');
                setPassword('');
            }, 3000);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password.');
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        if (mode === 'reset-password') return handleResetPassword(e);
        e.preventDefault();
        setError('');

        if (mode === 'forgot') {
            if (!email) { setError('Please enter your email to reset password'); return; }
            setLoading(true);
            try {
                await API.post('/auth/forgot-password', { email });
                setError('✅ Password reset link has been sent to your email.');
                setLoading(false);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to send reset email.');
                setLoading(false);
            }
            return;
        }

        if (mode === 'register') {
            if (!name || !companyName || !phone) { setError('Please fill in all required fields'); return; }
            if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
            if (!/[A-Z]/.test(password)) { setError('Password must contain at least one uppercase letter'); return; }
        }

        setLoading(true);
        try {
            const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
            const payload = mode === 'login'
                ? { email, password }
                : { name, email, password, role, company_name: companyName, phone };

            const { data } = await API.post(endpoint, payload);

            if (mode === 'register') {
                setError('✅ ' + (data.message || 'Account created successfully! Please login.'));
                setName('');
                setEmail('');
                setPassword('');
                setMode('login');
                setLoading(false);
                return;
            }

            // Persist user info (including token) for session
            localStorage.setItem('userInfo', JSON.stringify(data));
            onLogin(data.role, data);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '8px 12px', /* ezconn py-2 equivalent (8px top/bottom) */
        borderRadius: 8,
        border: '1px solid #d0d5dd', /* ezconn 1px border */
        background: '#fff',
        color: '#1a2740',
        fontFamily: 'inherit',
        lineHeight: '20px', /* ezconn exact line height */
        outline: 'none',
        transition: 'border-color .2s, box-shadow .2s',
        /* Default 16px to prevent iOS zoom, responsive via className */
    };

    return (
        <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
            {/* Inject minimal responsive class for font-size and layout */}
            <style>{`
                .ezconn-input { font-size: 16px; }
                @media (min-width: 640px) { .ezconn-input { font-size: 14px; } }
                
                @media (max-width: 1023px) {
                    .right-gradient-panel { display: none !important; }
                }
            `}</style>

            {/* ─── Left Side: Login Form (Significantly Wider) ─── */}
            <div style={{
                flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                background: '#f8f9fc', position: 'relative'
            }}>
                <div style={{ width: '100%', maxWidth: 520, padding: '0 32px' }}>

                    {/* Heading replaced by Logo as per swap */}
                    <div style={{ textAlign: 'center', marginBottom: 32, marginTop: 24 }}>
                        <img src="/logo.png" alt="Allin Logistics" style={{
                            width: 180, height: 180, objectFit: 'contain',
                            filter: 'drop-shadow(0 6px 16px rgba(0,0,0,.15))'
                        }} />
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <p style={{ fontSize: 13, color: '#8691b0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
                            {mode === 'login' ? 'Portal Login' : mode === 'forgot' ? 'Reset Password' : mode === 'reset-password' ? 'Set New Password' : 'Create Account'}
                        </p>
                        <p style={{ fontSize: 15, color: '#8691b0', fontWeight: 400 }}>
                            {mode === 'login' ? 'Login into your account to continue' : mode === 'forgot' ? 'Enter your email to receive a reset link' : mode === 'reset-password' ? 'Please enter your new secure password' : 'Sign up to start managing your cargo'}
                        </p>
                    </div>

                    {/* Success/Error Message */}
                    {error && (
                        <div style={{
                            padding: '10px 14px', borderRadius: 8, 
                            background: error.includes('✅') ? '#f0fdf4' : '#fff5f5', 
                            border: `1px solid ${error.includes('✅') ? '#bbf7d0' : '#fecaca'}`,
                            color: error.includes('✅') ? '#15803d' : '#dc2626', 
                            fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
                        }}>
                            <span>{error.includes('✅') ? '✓' : '⚠'}</span> {error}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {mode === 'register' && (
                          <>
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1a2740', marginBottom: 7 }}>Full Name</label>
                                <input type="text" value={name} onChange={e => setName(e.target.value)}
                                    placeholder="Enter your name" className="ezconn-input" style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1a2740', marginBottom: 7 }}>Company Name</label>
                                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                                    placeholder="Enter company name" className="ezconn-input" style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1a2740', marginBottom: 7 }}>Phone Number</label>
                                <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                                    placeholder="+1 234..." className="ezconn-input" style={inputStyle} />
                            </div>
                          </>
                        )}

                        {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1a2740', marginBottom: 7 }}>Email</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    placeholder="m@example.com" className="ezconn-input" style={inputStyle} />
                            </div>
                        )}



                        {mode !== 'forgot' && (
                        <div style={{ marginBottom: 8 }}>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1a2740', marginBottom: 7 }}>
                                {mode === 'reset-password' ? 'New Password' : 'Password'}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••" className="ezconn-input" style={{ ...inputStyle, paddingRight: 44 }} />
                                <span onClick={() => setShowPwd(!showPwd)} style={{
                                    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                                    cursor: 'pointer', color: '#8691b0', userSelect: 'none'
                                }}>
                                    {showPwd ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    )}
                                </span>
                            </div>
                        </div>
                        )}

                        {/* Forgot Password */}
                        {mode === 'login' && (
                            <div style={{ textAlign: 'right', marginBottom: 20 }}>
                                <span onClick={() => { setMode('forgot'); setError(''); }} style={{ fontSize: 13, color: '#3A75C4', fontWeight: 500, cursor: 'pointer' }}>Forgot Password?</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button type="submit" disabled={loading}
                            style={{
                                width: '100%', padding: '10px 20px', borderRadius: 8, border: 'none',
                                background: loading ? '#a0b0c8' : '#3A75C4',
                                color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
                                transition: 'all .2s', marginTop: mode === 'reset-password' ? 20 : (mode === 'register' ? 20 : (mode === 'forgot' ? 20 : 0))
                            }}>
                            {loading ? 'Processing...' : (mode === 'login' ? 'Login' : mode === 'forgot' ? 'Send Reset Link' : mode === 'reset-password' ? 'Update Password' : 'Create Account')}
                        </button>

                        <div style={{ textAlign: 'center', marginTop: 20 }}>
                            <p style={{ fontSize: 14, color: '#8691b0' }}>
                                {mode === 'login' ? "Don't have an account? " : mode === 'forgot' ? "Remember your password? " : "Already have an account? "}
                                <span onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                                    style={{ color: '#3A75C4', fontWeight: 600, cursor: 'pointer' }}>
                                    {mode === 'login' ? 'Sign up' : 'Login instead'}
                                </span>
                            </p>
                        </div>
                    </form>

                    {/* Footer */}
                    <div style={{ textAlign: 'center', marginTop: 40 }}>
                        <p style={{ fontSize: 13, color: '#a0b0c8' }}>
                            © 2026 All in Logistics. <span style={{ color: '#3A75C4', cursor: 'pointer' }}>Privacy Policy</span> | <span style={{ color: '#3A75C4', cursor: 'pointer' }}>Terms of Service</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* ─── Right Side: Gradient with Logo (Hidden on Mobile, 50% on Desktop) ─── */}
            <div className="right-gradient-panel" style={{
                flex: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                background: 'linear-gradient(135deg, #3A75C4 0%, #6BA3D6 40%, #8b7dd6 70%, #a78bfa 100%)',
                position: 'relative', overflow: 'hidden'
            }}>
                {/* Floating circles */}
                <div style={{
                    position: 'absolute', top: -100, right: -100, width: 350, height: 350, borderRadius: '50%',
                    background: 'rgba(255,255,255,.06)', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: -80, left: -80, width: 280, height: 280, borderRadius: '50%',
                    background: 'rgba(255,255,255,.05)', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '30%', left: '15%', width: 120, height: 120, borderRadius: '50%',
                    background: 'rgba(255,255,255,.04)', pointerEvents: 'none'
                }} />

                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <h1 style={{
                        fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1.1,
                        fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 24,
                        textShadow: '0 2px 10px rgba(0,0,0,.2)'
                    }}>
                        WELCOME TO<br />
                        <span style={{ fontSize: 36 }}>ALL IN LOGISTICS</span>
                    </h1>
                    <h2 style={{
                        fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-.5px', marginBottom: 8,
                        textShadow: '0 2px 12px rgba(0,0,0,.15)'
                    }}>Warehouse Management</h2>
                    <p style={{
                        fontSize: 14, color: 'rgba(255,255,255,.7)', fontWeight: 500, maxWidth: 300
                    }}>Track inventory, manage deliveries, and streamline your logistics operations</p>
                </div>
            </div>

            {/* Spinner keyframes */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
